import type { SupabaseClient } from '@supabase/supabase-js';

export type ShareTypeCore = 'contact' | 'event';

export interface ShareLinkRowCore {
    id: string;
    token: string;
    type: ShareTypeCore;
    contact_id?: string | null;
    event_id?: string | null;
    payload?: any;
    expires_at: string;
    created_at: string;
}

function generateTokenLocal(length = 48): string {
    const alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let out = '';
    const cryptoObj: Crypto | undefined = (globalThis as any).crypto;
    if (cryptoObj && 'getRandomValues' in cryptoObj) {
        const arr = new Uint32Array(length);
        (cryptoObj as any).getRandomValues(arr);
        for (let i = 0; i < length; i++) out += alphabet[arr[i] % alphabet.length];
        return out;
    }
    for (let i = 0; i < length; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
    return out;
}

export async function createContactShareCore(sb: SupabaseClient, contactId: string, days: number = 7): Promise<string> {
    if (!contactId) throw new Error('createContactShareCore: contactId is required');
    const token = generateTokenLocal(48);
    const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await sb.from('share_links').insert({
        token,
        type: 'contact',
        contact_id: contactId,
        expires_at: expiresAt,
    } as any);
    if (error) throw error;
    return token;
}

export async function createEventShareByIdCore(sb: SupabaseClient, eventId: string, days: number = 7): Promise<string> {
    if (!eventId) throw new Error('createEventShareByIdCore: eventId is required');
    const token = generateTokenLocal(48);
    const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await sb.from('share_links').insert({
        token,
        type: 'event',
        event_id: eventId,
        expires_at: expiresAt,
    } as any);
    if (error) throw error;
    return token;
}

export async function createEventShareFromPayloadCore(sb: SupabaseClient, payload: any, days: number = 7): Promise<string> {
    const token = generateTokenLocal(48);
    const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await sb.from('share_links').insert({
        token,
        type: 'event',
        payload,
        expires_at: expiresAt,
    } as any);
    if (error) throw error;
    return token;
}

export async function resolveShareCore(sb: SupabaseClient, token: string): Promise<{ type: ShareTypeCore; contact?: any; event?: any; payload?: any } | null> {
    if (!token) throw new Error('resolveShareCore: token is required');
    const { data, error } = await sb.rpc('resolve_share', { p_token: token });
    if (error) throw error;
    if (!data || data.length === 0) return null;
    const row = data[0] as any;
    return { type: row.type, contact: row.contact || undefined, event: row.event || undefined, payload: row.payload || undefined };
}
